<template>
  <div v-bind="$attrs">
    <ErrorMessage :error="error" />
    <div class="relative" @click="toggle">
      <input
        :id="id"
        ref="input"
        v-mask="'####-##-##'"
        :value="value"
        :name="name"
        :autocomplete="autocomplete"
        class="input"
        :class="{ 'has-error': error }"
        placeholder="Дата рождения* (например: 1990-05-14)"
        @change="changeDate"
      />
      <svg
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
        class="absolute w-6 h-6 top-2 right-3 text-gray-500"
        @click="input.focus()"
      >
        <path
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="2"
          d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
        />
      </svg>
    </div>
  </div>

  <Teleport to="body">
    <Transition name="datepicker-transition">
      <div
        v-if="open"
        v-click-outside="clickOutside"
        class="absolute bg-black border border-gray-800 rounded-lg shadow z-50 mt-12 px-4 py-2"
        :style="[position, { width: '17rem' }]"
      >
        <div class="flex justify-between items-center mb-2">
          <div class="cursor-pointer rounded-full hover:bg-gray-500 px-3 py-0.5" @click.stop="toggleSelectedPage">
            <template v-if="selectedPage == 'days'">
              <span class="text-lg font-bold text-white">{{ MONTH_NAMES[selectedMonth] }}</span>
              <span class="ml-1 text-lg text-white font-normal">{{ selectedYear }}</span>
            </template>
            <span v-else-if="selectedPage == 'months'" class="text-lg text-white">{{ selectedYear }}</span>
            <div v-else class="text-lg text-white font-normal">{{ yearPeriod }} - {{ yearPeriod + 11 }}</div>
          </div>
          <div>
            <button
              type="button"
              class="inline-flex outline-none cursor-pointer rounded-full hover:bg-gray-500 p-1"
              @click="clickPrevious"
            >
              <svg class="h-6 w-6 text-gray-300 inline-flex" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            <button
              type="button"
              class="inline-flex outline-none cursor-pointer rounded-full hover:bg-gray-500 p-1"
              @click="clickNext"
            >
              <svg class="h-6 w-6 text-gray-300 inline-flex" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 5l7 7-7 7" />
              </svg>
            </button>
          </div>
        </div>

        <template v-if="selectedPage == 'days'">
          <div class="grid grid-cols-7 mb-3 -mx-1">
            <div v-for="(day, index) in DAYS" :key="index" class="text-gray-300 font-medium text-center text-xs">
              {{ day }}
            </div>
          </div>

          <div class="grid grid-cols-7 -mx-1">
            <div
              v-for="day in prevousMonthDays"
              :key="day"
              class="border border-transparent text-center text-sm leading-7 text-gray-500 m-0.5"
            >
              {{ day }}
            </div>
            <div
              v-for="day in days"
              :key="day"
              class="border cursor-pointer text-center text-sm rounded-full leading-7 m-0.5"
              :class="{
                'border-blue-600': isToday(selectedYear, selectedMonth, day),
                'border-transparent': !isToday(selectedYear, selectedMonth, day),
                'bg-blue-500 text-white hover:bg-opacity-75': isSelected(selectedYear, selectedMonth, day),
                'hover:bg-blue-400': !isSelected(selectedYear, selectedMonth, day),
              }"
              @click="selectDay(day)"
            >
              {{ day }}
            </div>
            <div
              v-for="day in nextMonthDays"
              :key="day"
              class="border border-transparent text-center text-sm leading-7 text-gray-500 m-0.5"
            >
              {{ day }}
            </div>
          </div>
        </template>
        <div v-else-if="selectedPage == 'months'" class="grid grid-cols-4">
          <div
            v-for="(month, index) in MONTH_SHORT_NAMES"
            :key="index"
            class="h-10 border cursor-pointer text-sm rounded text-center leading-10 hover:bg-blue-400 m-0.5"
            :class="{
              'border-blue-600': isToday(selectedYear, index),
              'border-transparent': !isToday(selectedYear, index),
              'bg-blue-500 text-white hover:bg-opacity-75': isSelected(selectedYear, index),
              'hover:bg-blue-400': !isSelected(selectedYear, index),
            }"
            @click.stop="selectMonth(index)"
          >
            {{ month }}
          </div>
        </div>
        <div v-else class="grid grid-cols-4">
          <div
            v-for="(year, index) in 12"
            :key="year"
            class="h-10 border cursor-pointer text-sm rounded text-center leading-10 hover:bg-blue-400 m-0.5"
            :class="{
              'border-blue-600': isToday(yearPeriod + index),
              'border-transparent': !isToday(yearPeriod + index),
              'bg-blue-500 text-white hover:bg-opacity-75': isSelected(yearPeriod + index),
              'hover:bg-blue-400': !isSelected(yearPeriod + index),
            }"
            @click.stop="selectYear(yearPeriod + index)"
          >
            {{ yearPeriod + index }}
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<script setup>
import { computed, getCurrentInstance, ref, watch } from 'vue'

import ErrorMessage from './ErrorMessage'

const props = defineProps({
  modelValue: {
    type: [String, Number],
    default: null,
  },
  label: {
    type: String,
    default: null,
  },
  name: {
    type: String,
    default: undefined,
  },
  autocomplete: {
    type: String,
    default: undefined,
  },
  required: {
    type: Boolean,
    default: false,
  },
  error: {
    type: String,
    default: undefined,
  },
})

const emit = defineEmits(['update:modelValue', 'update:error'])

const { uid } = getCurrentInstance()
const id = computed(() => `datepicker-${uid}`)

const value = ref(props.modelValue)
watch(value, (val) => {
  emit('update:modelValue', val)
  emit('update:error', undefined)
})
watch(
  () => props.modelValue,
  (val) => (value.value = val),
)
function changeDate(e) {
  value.value = e.target.value
}

watch(value, (val) => {
  emit('update:modelValue', val)
  emit('update:error', undefined)
})

watch(
  () => props.modelValue,
  (val) => (value.value = val),
)

// prettier-ignore
const MONTH_NAMES = ['Январь', 'Февраль', 'Март', 'Апрель', 'Май', 'Июнь', 'Июль', 'Август', 'Сентябрь',
  'Октябрь', 'Ноябрь', 'Декабрь']
const MONTH_SHORT_NAMES = ['Янв', 'Фев', 'Мар', 'Апр', 'Май', 'Июн', 'Июл', 'Авг', 'Сен', 'Окт', 'Ноя', 'Дек']
const DAYS = ['Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Вс']

const input = ref()
const open = ref(false)
const position = ref({})
function toggle() {
  if (open.value) {
    open.value = false
  } else {
    const { left, top } = input.value.getBoundingClientRect()
    position.value = { left: `${left + window.scrollX}px`, top: `${top + window.scrollY}px` }
    open.value = true
  }
}
function clickOutside({ target }) {
  if (input.value.parentNode.contains(target) || input.value === target) return

  open.value = false
}

const selectedDay = ref()
const selectedMonth = ref(new Date().getMonth())
const selectedYear = ref(new Date().getFullYear())
const yearPeriod = ref(selectedYear.value - 5)
const selectedPage = ref('days')
function clickPrevious() {
  if (selectedPage.value == 'days') {
    if (selectedMonth.value == 0) {
      selectedYear.value--
      selectedMonth.value = 11
    } else {
      selectedMonth.value--
    }

    getDays()
  } else if (selectedPage.value == 'months') {
    selectedYear.value--
  } else {
    yearPeriod.value -= 12
  }
}
function clickNext() {
  if (selectedPage.value == 'days') {
    if (selectedMonth.value == 11) {
      selectedMonth.value = 0
      selectedYear.value++
    } else {
      selectedMonth.value++
    }

    getDays()
  } else if (selectedPage.value == 'months') {
    selectedYear.value++
  } else {
    yearPeriod.value += 12
  }
}
function toggleSelectedPage() {
  selectedPage.value = selectedPage.value == 'days' ? 'months' : selectedPage.value == 'months' ? 'years' : 'days'
}

const days = ref()
const prevousMonthDays = ref([])
const nextMonthDays = ref()
function getDays() {
  let prevousMonth = []
  let lastWeek = new Date(selectedYear.value, selectedMonth.value).getDay() - 1
  const lastDay = new Date(selectedYear.value, selectedMonth.value, 0).getDate()

  if (lastWeek == -1) {
    lastWeek = 6
  }
  while (lastWeek > 0) {
    prevousMonth.push(lastDay - lastWeek-- + 1)
  }

  prevousMonthDays.value = prevousMonth
  days.value = new Date(selectedYear.value, selectedMonth.value + 1, 0).getDate()
  nextMonthDays.value = 42 - days.value - prevousMonthDays.value.length
}

function isToday(year = new Date().getFullYear(), month = new Date().getMonth(), day = new Date().getDate()) {
  return new Date().toDateString() === new Date(year, month, day).toDateString()
}
function isSelected(year = selectedYear.value, month = selectedMonth.value, day = selectedDay.value) {
  return value.value === formatDate(new Date(year, month, day))
}
function selectDay(day) {
  selectedDay.value = day
  value.value = formatDate(new Date(selectedYear.value, selectedMonth.value, day))
  open.value = false
}
function selectMonth(month) {
  selectedMonth.value = month
  selectedPage.value = 'days'
  getDays()
}
function selectYear(year) {
  selectedYear.value = year
  selectedPage.value = 'months'
}

function formatDate(date) {
  const formattedDay = `0${date.getDate()}`.slice(-2)
  const formattedMonth = `0${parseInt(date.getMonth()) + 1}`.slice(-2)
  const formattedYear = date.getFullYear()

  return `${formattedYear}-${formattedMonth}-${formattedDay}`
}

getDays()
</script>

<style lang="postcss" scoped>
.input {
  @apply w-full py-4 px-3;
  @apply leading-8 text-base outline-none rounded-md;
  @apply bg-white border border-gray-500 text-black;
  @apply transition-all duration-300;
}
.input.has-error {
  @apply border-red-600;
}
.input.has-error:-webkit-autofill:focus {
  @apply border-red-600;
}
.input::-webkit-calendar-picker-indicator {
  @apply w-0 h-0;
}

.datepicker-transition-enter-active,
.datepicker-transition-leave-active {
  transition: transform 0.2s;
  transform-origin: top left;
}

.datepicker-transition-enter-from,
.datepicker-transition-leave-to {
  transform: scale(0);
}
</style>
